import { createStore } from 'vuex'
import axios from 'axios'
import * as Config from "../config"
import Cookies from "js-cookie"
import moment from 'moment'
import createPersistedState from 'vuex-persistedstate'
import deliveryDetail from './deliveryDetail'
import deliveryHistory from './deliveryHistory'
import activeDeliveries from './activeDeliveries'
import courierData from './courierData'
import zonesAndCompanies from './zonesAndCompanies'

const ACTIVE_SERVICE_TTL = 6 * 60 * 60 * 1000
const MAX_ACTIVE_SERVICES = 20
const HISTORY_TTL = 30 * 60 * 1000
const MAX_HISTORY_DELIVERIES = 50
const FINAL_STATUS_IDS = [13, 18]

const isFinalStatus = (serviceData) => {
  const statusId = Number(serviceData?.id_type_status_service || serviceData?.type_status_service?.id)
  return FINAL_STATUS_IDS.includes(statusId)
}

const sanitizePersistedState = (value) => {
  try {
    const parsed = JSON.parse(value)
    const now = Date.now()

    if (parsed.activeDeliveries?.services) {
      const services = parsed.activeDeliveries.services || {}
      const lastUpdated = parsed.activeDeliveries.lastUpdated || {}
      const entries = Object.entries(services)
        .filter(([serviceId, serviceData]) => {
          const updatedAt = lastUpdated[serviceId] || 0
          return !isFinalStatus(serviceData) && now - updatedAt <= ACTIVE_SERVICE_TTL
        })
        .sort(([a], [b]) => (lastUpdated[b] || 0) - (lastUpdated[a] || 0))
        .slice(0, MAX_ACTIVE_SERVICES)

      parsed.activeDeliveries.services = entries.reduce((acc, [serviceId, serviceData]) => {
        acc[serviceId] = serviceData
        return acc
      }, {})

      parsed.activeDeliveries.lastUpdated = entries.reduce((acc, [serviceId]) => {
        acc[serviceId] = lastUpdated[serviceId]
        return acc
      }, {})
    }

    if (parsed.deliveryHistory) {
      const isExpired = !parsed.deliveryHistory.lastUpdated || now - parsed.deliveryHistory.lastUpdated > HISTORY_TTL
      parsed.deliveryHistory.deliveries = isExpired
        ? null
        : Array.isArray(parsed.deliveryHistory.deliveries)
          ? parsed.deliveryHistory.deliveries.slice(0, MAX_HISTORY_DELIVERIES)
          : null
      parsed.deliveryHistory.lastUpdated = isExpired ? null : parsed.deliveryHistory.lastUpdated
    }

    return JSON.stringify(parsed)
  } catch (e) {
    console.warn('Error sanitizando estado persistido:', e)
    return value
  }
}

// Configuración global de Axios
axios.defaults.headers.common["Authorization"] = "Bearer " + localStorage.getItem("access_token")
axios.defaults.timeout = 30000 // Aumentar timeout a 30 segundos

// Interceptor para manejar errores de red
axios.interceptors.response.use(
  response => response,
  error => {
    // Manejar errores de timeout y red
    if (error.code === 'ECONNABORTED' || !error.response) {
      console.error('Error de conexión o timeout:', error.message);
      // Actualizar el estado online si corresponde
      if (typeof store !== 'undefined' && store.dispatch) {
        store.dispatch('setOnlineStatus', false);
      }
    }
    return Promise.reject(error);
  }
)

// Crear la instancia del store
const store = createStore({
  plugins: [
    createPersistedState({
      key: 'ami-driver-state',
      // zonesAndCompanies se excluye intencionalmente: puede crecer mucho
      // (zonas + compañías de la API) y superar el límite de 5MB del localStorage.
      // Estos datos siempre se recargan desde el servidor al abrir la app.
      paths: ['activeDeliveries', 'deliveryHistory'],
      storage: {
        getItem: (key) => {
          try {
            return window.localStorage.getItem(key)
          } catch (e) {
            console.warn('Error leyendo localStorage, limpiando caché...', e)
            window.localStorage.removeItem('ami-driver-state')
            return null
          }
        },
        setItem: (key, value) => {
          const sanitizedValue = sanitizePersistedState(value)
          try {
            window.localStorage.setItem(key, sanitizedValue)
          } catch (e) {
            // Cuota excedida: limpiar la clave y reintentar una vez
            console.warn('Cuota de localStorage excedida. Limpiando ami-driver-state...', e)
            window.localStorage.removeItem('ami-driver-state')
            try {
              window.localStorage.setItem(key, sanitizedValue)
            } catch (e2) {
              console.error('No se pudo guardar en localStorage incluso después de limpiar:', e2)
            }
          }
        },
        removeItem: (key) => window.localStorage.removeItem(key)
      }
    })
  ],
  modules: {
    deliveryDetail,
    deliveryHistory,
    activeDeliveries,
    courierData,
    zonesAndCompanies
  },
  state: {
    // Estado de la aplicación
    online: navigator.onLine, // Estado de conexión a Internet
    lastOnlineCheck: Date.now(), // Última vez que se verificó la conexión
    notifications: [], // Lista de notificaciones
    isLoading: false, // Estado de carga
    error: null // Error en la solicitud
  },
  actions: {
    // Acción para obtener notificaciones
    async fetchNotifications({ commit }) {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      
      try {
        // Conectar a la API para obtener notificaciones
        console.log('Fetching notifications from API...');
        const response = await axios.get(Config.apiPath + '/app/notification/all/optimized', {
          params: {
            page: 1,
            limit: 20
          }
        });
        
        console.log('API Response:', response.data);
        
        if (response.data && response.data.code === 200) {
          // Verificar la estructura de la respuesta
          let notifications = [];
          
          if (response.data.notifications) {
            notifications = response.data.notifications;
          } else if (response.data.data?.data && Array.isArray(response.data.data.data)) {
            notifications = response.data.data.data;
          } else if (response.data.data) {
            notifications = response.data.data;
          } else if (Array.isArray(response.data)) {
            notifications = response.data;
          }
          
          console.log('Processed notifications:', notifications);
          commit('SET_NOTIFICATIONS', notifications);
        } else {
          // Si no hay datos o hay un error, establecer un array vacío
          console.log('No notifications found or error in response');
          commit('SET_NOTIFICATIONS', []);
        }
      } catch (error) {
        console.error('Error al cargar notificaciones:', error);
        commit('SET_ERROR', error.message || 'Error al cargar notificaciones');
        commit('SET_NOTIFICATIONS', []);
      } finally {
        commit('SET_LOADING', false);
      }
    },
    
    // Acción para marcar una notificación como leída
    async markNotificationAsRead({ commit }, id) {
      try {
        // Conectar a la API para marcar notificación como leída
        const response = await axios.put(Config.apiPath + `/app/notification/${id}/read`);
        
        if (response.data && response.data.code === 200) {
          // Actualizar el estado local
          commit('UPDATE_NOTIFICATION', { 
            id, 
            changes: { read: true } 
          });
          
          return true;
        } else {
          console.error('Error en respuesta al marcar notificación como leída:', response.data);
          return false;
        }
      } catch (error) {
        console.error('Error al marcar notificación como leída:', error);
        return false;
      }
    },
    login({ commit }, form) {
      return new Promise((resolve, reject) => {
        axios.post(Config.apiPath + "/app/courier/login", form)
          .then(response => {
            if (response.data.code === 200) {
              const accessToken = response.data.auth.token
              const expires_in = moment().format("YYYY-MM-DD HH:mm")
              const expires_at = moment(expires_in).add(4, 'hours').format("YYYY-MM-DD HH:mm")
              const expires_at_date = new Date(expires_at)

              // Guardar en cookies
              Cookies.set("access_token", accessToken, { expires: expires_at_date })
              Cookies.set("id", response.data.id_courier, { expires: expires_at_date })
              Cookies.set("email", response.data.courier_email, { expires: expires_at_date })
              Cookies.set("full_name", response.data.full_name, { expires: expires_at_date })
              
              // Guardar también en localStorage para persistencia
              localStorage.setItem("access_token", accessToken)
              localStorage.setItem("id", response.data.id_courier)
              localStorage.setItem("email", response.data.courier_email)
              localStorage.setItem("expires_at", expires_at)
              
              resolve(response.data)
            } else {
              commit("LOGIN_FAIL1")
              reject(response.data)
            }
          })
          .catch(error => {
            reject(error)
          })
      })
    },
    
    logout() {
      // Limpiar cookies
      const cookies = Cookies.get()
      for (const cookie in cookies) {
        Cookies.remove(cookie)
      }
      
      // Limpiar localStorage
      localStorage.removeItem("access_token")
      localStorage.removeItem("id")
      localStorage.removeItem("email")
      localStorage.removeItem("expires_at")
    },

    getData({ commit }, form) {
      return new Promise((resolve, reject) => {
        axios.get(Config.apiPath + "/" + form.url, { params: form.params })
          .then(response => resolve(response.data))
          .catch(error => reject(error.response.data))
      })
    },

    getDataLite({ commit }, form) {
      return new Promise((resolve, reject) => {
        // Verificar conexión antes de hacer la petición
        if (!navigator.onLine) {
          commit('SET_ONLINE_STATUS', false);
          return reject({ error: 'Sin conexión a Internet' });
        }
        
        axios.get(Config.apiPath + "/" + form.url, {
          // Configuración específica para esta petición
          timeout: 30000 // 30 segundos de timeout
        })
          .then(response => {
            commit('SET_ONLINE_STATUS', true); // Actualizar estado online
            resolve(response.data);
          })
          .catch(error => {
            console.error('Error en getDataLite:', error);
            // Si es un error de red o timeout
            if (error.code === 'ECONNABORTED' || !error.response) {
              commit('SET_ONLINE_STATUS', false);
              reject({ error: 'Tiempo de espera agotado o error de red' });
            } else {
              // Otros errores con respuesta del servidor
              reject(error.response ? error.response.data : { error: error.message });
            }
          })
      })
    },

    createData({ commit }, form) {
      return new Promise((resolve, reject) => {
        axios.post(Config.apiPath + "/" + form.url, form.data)
          .then(response => resolve(response.data))
          .catch(error => reject(error.response.data))
      })
    },

    updateData({ commit }, form) {
      return new Promise((resolve, reject) => {
        axios.put(Config.apiPath + "/" + form.url + '/' + form.id, form.data)
          .then(response => resolve(response.data))
          .catch(error => reject(error.response.data))
      })
    },

    updateDataClean({ commit }, form) {
      return new Promise((resolve, reject) => {
        axios.put(Config.apiPath + "/" + form.url, form.data, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${Cookies.get('access_token')}`
          }
        })
          .then(response => resolve(response.data))
          .catch(error => reject(error.response.data))
      })
    },

    changeStatusData({ commit }, form) {
      return new Promise((resolve, reject) => {
        axios.put(Config.apiPath + "/" + form.url + "/" + form.id + "/" + form.status, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${Cookies.get('access_token')}`
          }
        })
          .then(response => resolve(response.data))
          .catch(error => reject(error.response.data))
      })
    },

    // Actualizar el estado de conexión a Internet
    setOnlineStatus({ commit }, status) {
      commit('SET_ONLINE_STATUS', status);
    },

    getFile({ commit }, form) {
      return new Promise((resolve, reject) => {
        axios.get(Config.apiPath + "/upload/all?active=1&type=" + form.type + "&page=" + form.page, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${Cookies.get('access_token')}`
          }
        })
          .then(response => resolve(response.data))
          .catch(error => reject(error.response.data))
      })
    },

    UploadFile({ commit }, form) {
      return new Promise((resolve, reject) => {
        axios.post(Config.apiPath + "/upload/file", form, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${Cookies.get('access_token')}`
          }
        })
          .then(response => resolve(response.data))
          .catch(error => reject(error.response.data))
      })
    }
  },
  mutations: {
    // Mutaciones para actualizar el estado
    SET_ONLINE_STATUS(state, status) {
      state.online = status;
      state.lastOnlineCheck = Date.now();
    },
    // Mutaciones para notificaciones
    SET_LOADING(state, status) {
      state.isLoading = status;
    },
    SET_ERROR(state, error) {
      state.error = error;
    },
    SET_NOTIFICATIONS(state, notifications) {
      state.notifications = notifications;
    },
    UPDATE_NOTIFICATION(state, { id, changes }) {
      state.notifications = state.notifications.map(notification => 
        notification.id === id ? { ...notification, ...changes } : notification
      );
    }
  },
  getters: {
    // Retorna si estamos en línea
    isOnline: state => state.online,
    
    // Getters para notificaciones
    getNotifications: state => state.notifications,
    isLoading: state => state.isLoading,
    getError: state => state.error
  }
})

// Exportar la instancia del store
export default store
